import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { MapPin, Loader2, Navigation, X } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const LocationPicker = ({ 
  value, 
  onChange, 
  onSelect, 
  placeholder = "Enter location",
  icon: Icon = MapPin,
  iconColor = "text-slate-400",
  className = "",
  showGpsButton = true,
  showMapPicker = true,
  ...props 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGpsLoading, setIsGpsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 }); // NYC default
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const geocoder = useRef(null);
  const sessionToken = useRef(null);

  // Initialize Google Maps services
  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        const dummyDiv = document.createElement('div');
        placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
        geocoder.current = new window.google.maps.Geocoder();
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
      }
    };

    if (window.google && window.google.maps) {
      loadGoogleMaps();
    } else {
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.maps) {
          loadGoogleMaps();
          clearInterval(checkGoogle);
        }
      }, 100);
      return () => clearInterval(checkGoogle);
    }
  }, []);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize map when modal opens
  useEffect(() => {
    if (showMapModal && mapRef.current && window.google) {
      const map = new window.google.maps.Map(mapRef.current, {
        center: mapCenter,
        zoom: 15,
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      
      mapInstanceRef.current = map;
      
      // Add marker
      const marker = new window.google.maps.Marker({
        position: mapCenter,
        map: map,
        draggable: true,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#DC2626',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
        },
      });
      
      markerRef.current = marker;
      
      // Update marker position on map click
      map.addListener('click', (e) => {
        marker.setPosition(e.latLng);
        reverseGeocode(e.latLng.lat(), e.latLng.lng());
      });
      
      // Update on marker drag
      marker.addListener('dragend', () => {
        const pos = marker.getPosition();
        reverseGeocode(pos.lat(), pos.lng());
      });
    }
  }, [showMapModal, mapCenter]);

  const reverseGeocode = useCallback((lat, lng) => {
    if (!geocoder.current) return;
    
    geocoder.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setSelectedLocation({
          lat,
          lng,
          address: results[0].formatted_address,
        });
      }
    });
  }, []);

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsGpsLoading(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setMapCenter({ lat: latitude, lng: longitude });
        
        // Reverse geocode to get address
        if (geocoder.current) {
          geocoder.current.geocode(
            { location: { lat: latitude, lng: longitude } },
            (results, status) => {
              setIsGpsLoading(false);
              if (status === 'OK' && results[0]) {
                const location = {
                  lat: latitude,
                  lng: longitude,
                  address: results[0].formatted_address,
                };
                setInputValue(location.address);
                onChange?.(location.address);
                onSelect?.(location);
              }
            }
          );
        } else {
          setIsGpsLoading(false);
        }
      },
      (error) => {
        setIsGpsLoading(false);
        console.error('Geolocation error:', error);
        alert('Unable to get your location. Please enter manually.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [onChange, onSelect]);

  const fetchSuggestions = useCallback(async (input) => {
    if (!input || input.length < 3 || !autocompleteService.current) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);

    try {
      const request = {
        input,
        sessionToken: sessionToken.current,
      };

      autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
        setIsLoading(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions.slice(0, 5));
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
        }
      });
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setIsLoading(false);
      setSuggestions([]);
    }
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);
    fetchSuggestions(newValue);
  };

  const handleSelectSuggestion = (suggestion) => {
    if (!placesService.current) return;

    const request = {
      placeId: suggestion.place_id,
      fields: ['geometry', 'formatted_address', 'name'],
      sessionToken: sessionToken.current,
    };

    placesService.current.getDetails(request, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        const location = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          address: place.formatted_address || suggestion.description,
          name: place.name,
        };

        setInputValue(location.address);
        onChange?.(location.address);
        onSelect?.(location);
        setShowSuggestions(false);
        setMapCenter({ lat: location.lat, lng: location.lng });
        
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
      }
    });
  };

  const handleMapConfirm = () => {
    if (selectedLocation) {
      setInputValue(selectedLocation.address);
      onChange?.(selectedLocation.address);
      onSelect?.(selectedLocation);
    }
    setShowMapModal(false);
  };

  const openMapPicker = () => {
    // Get current location first for map center
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMapCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setShowMapModal(true);
        },
        () => {
          setShowMapModal(true);
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    } else {
      setShowMapModal(true);
    }
  };

  return (
    <div className="relative">
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <Icon className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${iconColor}`} />
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder={placeholder}
            className={`pl-10 pr-10 h-12 ${className}`}
            data-testid="location-input"
            {...props}
          />
          {isLoading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
          )}
        </div>
        
        {/* GPS Button */}
        {showGpsButton && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-12 w-12 flex-shrink-0"
            onClick={getCurrentLocation}
            disabled={isGpsLoading}
            title="Use current location"
            data-testid="gps-btn"
          >
            {isGpsLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Navigation className="w-5 h-5 text-red-600" />
            )}
          </Button>
        )}
        
        {/* Map Picker Button */}
        {showMapPicker && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-12 w-12 flex-shrink-0"
            onClick={openMapPicker}
            title="Pick on map"
            data-testid="map-picker-btn"
          >
            <MapPin className="w-5 h-5 text-red-600" />
          </Button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden"
        >
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.place_id}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700 flex items-start gap-3 transition-colors"
              data-testid="location-suggestion"
            >
              <MapPin className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium text-slate-900 dark:text-white text-sm">
                  {suggestion.structured_formatting?.main_text || suggestion.description}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {suggestion.structured_formatting?.secondary_text || ''}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Map Picker Modal */}
      {showMapModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b dark:border-slate-700">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Drop Pin on Map
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowMapModal(false)}
                data-testid="close-map-modal"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Map */}
            <div 
              ref={mapRef} 
              className="w-full h-80"
              data-testid="pin-drop-map"
            />
            
            {/* Selected Location */}
            {selectedLocation && (
              <div className="p-4 bg-slate-50 dark:bg-slate-800 border-t dark:border-slate-700">
                <p className="text-sm text-slate-600 dark:text-slate-300 flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  {selectedLocation.address}
                </p>
              </div>
            )}
            
            {/* Footer */}
            <div className="flex gap-3 p-4 border-t dark:border-slate-700">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowMapModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleMapConfirm}
                disabled={!selectedLocation}
                data-testid="confirm-location-btn"
              >
                Confirm Location
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
